import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, Legend, CartesianGrid, ResponsiveContainer,
} from 'recharts';
import {
  Tags, Boxes, PackageOpen, PackageMinus, Layers, AlertTriangle,
  Clock, Building2, FileSpreadsheet, FileText, FileDown, RotateCcw,
} from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import StatCard from '../../components/ui/StatCard';
import { useAuth } from '../../context/AuthContext';

const DATE_PRESETS = [
  { key: 'today',      label: 'Today' },
  { key: 'week',       label: 'This Week' },
  { key: 'month',      label: 'This Month' },
  { key: 'last_month', label: 'Last Month' },
  { key: 'custom',     label: 'Custom' },
];

const TX_TYPES = [
  { key: 'all',     label: 'All' },
  { key: 'inward',  label: 'Inward' },
  { key: 'outward', label: 'Outward' },
];

// Categorical palette — distinguishable in order, anchored on the Ziplogyx red.
const PALETTE = ['#E8192C', '#0F766E', '#1D4ED8', '#B45309', '#7C3AED', '#0891B2', '#BE185D', '#4D7C0F'];

const emptyFilters = {
  preset: 'month', from: '', to: '',
  client: '', category: '', projectName: '', partNumber: '',
  transactionType: 'all', search: '',
};

const nf = (n) => Number(n || 0).toLocaleString();

/* --------------------------------- shell --------------------------------- */

const Card = ({ title, subtitle, action, children, className = '' }) => (
  <div className={`zip-card p-5 ${className}`}>
    {(title || action) && (
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="font-semibold text-zip-text tracking-tight text-sm">{title}</h3>
          {subtitle && <p className="text-[11px] text-zip-muted mt-0.5">{subtitle}</p>}
        </div>
        {action}
      </div>
    )}
    {children}
  </div>
);

const ChartEmpty = ({ height = 240, text = 'No data for the selected filters' }) => (
  <div className="flex items-center justify-center text-zip-muted text-sm" style={{ height }}>
    {text}
  </div>
);

const ChartSkeleton = ({ height = 240 }) => (
  <div className="skeleton rounded" style={{ height }} />
);

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-zip-border rounded-lg px-3 py-2 text-xs font-mono shadow-lg">
      {label !== undefined && <div className="text-zip-muted mb-1">{label}</div>}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full inline-block" style={{ background: p.color || p.fill }} />
          <span className="text-zip-muted">{p.name}:</span>
          <span className="text-zip-text font-semibold">{nf(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

const axisTick = { fontSize: 11, fill: '#64748B', fontFamily: 'JetBrains Mono, monospace' };

/* -------------------------------- the page -------------------------------- */

const ReportsAnalytics = () => {
  const { can } = useAuth();
  const canExport = can('reports.export');

  const [filters, setFilters]   = useState(emptyFilters);
  const [applied, setApplied]   = useState(emptyFilters);
  const [options, setOptions]   = useState({ clients: [], categories: [], projects: [] });
  const [summary, setSummary]   = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [tx, setTx]             = useState({ rows: [], total: 0, page: 1, pages: 1, totals: {} });
  const [page, setPage]         = useState(1);
  const [loading, setLoading]   = useState(true);
  const [txLoading, setTxLoading] = useState(true);
  const [error, setError]       = useState(null);
  const [exporting, setExporting] = useState('');

  // Only send filters that carry a value; `custom` is not a server-side preset.
  const params = useMemo(() => {
    const p = {};
    if (applied.preset && applied.preset !== 'custom') p.preset = applied.preset;
    if (applied.preset === 'custom') {
      if (applied.from) p.from = applied.from;
      if (applied.to) p.to = applied.to;
    }
    ['client', 'category', 'projectName', 'partNumber', 'search'].forEach((k) => {
      if (applied[k]) p[k] = applied[k];
    });
    if (applied.transactionType !== 'all') p.transactionType = applied.transactionType;
    return p;
  }, [applied]);

  useEffect(() => {
    api.get('/reports/filter-options')
      .then((r) => setOptions(r.data.data))
      .catch(() => { /* dropdowns stay empty; the page still works */ });
  }, []);

  const loadOverview = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.get('/reports/summary', { params }),
      api.get('/reports/analytics', { params }),
    ])
      .then(([s, a]) => { setSummary(s.data.data); setAnalytics(a.data.data); })
      .catch((err) => setError(err.response?.data?.message || 'Could not load the report data'))
      .finally(() => setLoading(false));
  }, [params]);

  const loadTransactions = useCallback(() => {
    setTxLoading(true);
    api.get('/reports/transactions', { params: { ...params, page, limit: 25 } })
      .then((r) => setTx(r.data.data))
      .catch(() => setTx({ rows: [], total: 0, page: 1, pages: 1, totals: {} }))
      .finally(() => setTxLoading(false));
  }, [params, page]);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => { loadTransactions(); }, [loadTransactions]);

  const applyFilters = () => {
    if (filters.preset === 'custom' && filters.from && filters.to && filters.from > filters.to) {
      return toast.error('“From” date cannot be after the “To” date');
    }
    setPage(1);
    setApplied(filters);
  };

  const resetFilters = () => { setFilters(emptyFilters); setApplied(emptyFilters); setPage(1); };

  /* ------------------------------- exports ------------------------------- */

  const downloadServerFile = async (fmt) => {
    setExporting(fmt);
    try {
      const res = await api.get('/reports/export', {
        params: { ...params, format: fmt },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `ims-report-${new Date().toISOString().slice(0, 10)}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (err) {
      toast.error(err.response?.data?.message || `${fmt.toUpperCase()} export failed`);
    } finally {
      setExporting('');
    }
  };

  // PDF is produced in the browser from the same filtered rows, so it never
  // diverges from what the Excel/CSV export contains.
  const exportPdf = async () => {
    setExporting('pdf');
    try {
      const [{ default: jsPDF }, autoTableMod] = await Promise.all([
        import('jspdf'),
        import('jspdf-autotable'),
      ]);
      const autoTable = autoTableMod.default || autoTableMod.autoTable;

      const res = await api.get('/reports/transactions', { params: { ...params, page: 1, limit: 200 } });
      const rows = res.data.data.rows || [];
      if (rows.length === 0) { toast.error('Nothing to export for these filters'); return; }

      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      doc.setFontSize(14);
      doc.text('Ziplogyx IMS — Reports & Analytics', 40, 36);
      doc.setFontSize(9);
      doc.setTextColor(100);
      doc.text(
        `Generated ${format(new Date(), 'dd MMM yyyy HH:mm')}   ·   ${activeFilterSummary()}`,
        40, 52
      );

      autoTable(doc, {
        startY: 66,
        head: [['Date', 'Type', 'Ref', 'Entity', 'Item', 'Project', 'Part No.', 'Category', 'UOM', 'Inward', 'Outward', 'Status']],
        body: rows.map((r) => [
          r.date ? format(new Date(r.date), 'dd MMM yy') : '—',
          r.type, r.ref || '—', r.entityName || '—', r.itemName || '—',
          r.projectName || '—', r.partNumber || '—', r.category || '—', r.uom || '—',
          nf(r.inwardQty), nf(r.outwardQty), r.status || '—',
        ]),
        styles: { fontSize: 7, cellPadding: 3 },
        headStyles: { fillColor: [232, 25, 44], textColor: 255, fontSize: 7.5 },
        alternateRowStyles: { fillColor: [248, 250, 252] },
      });

      if (res.data.data.total > rows.length) {
        const y = doc.lastAutoTable.finalY + 16;
        doc.setFontSize(8);
        doc.text(
          `Showing the first ${rows.length} of ${res.data.data.total} rows. Use the Excel or CSV export for the complete set.`,
          40, y
        );
      }

      doc.save(`ims-report-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success('PDF downloaded');
    } catch {
      toast.error('PDF export failed');
    } finally {
      setExporting('');
    }
  };

  function activeFilterSummary() {
    const bits = [];
    const preset = DATE_PRESETS.find((p) => p.key === applied.preset);
    if (applied.preset === 'custom') bits.push(`${applied.from || 'start'} → ${applied.to || 'today'}`);
    else if (preset) bits.push(preset.label);
    if (applied.client) {
      const c = options.clients.find((x) => x._id === applied.client);
      if (c) bits.push(`Entity: ${c.name}`);
    }
    if (applied.category) bits.push(`Category: ${applied.category}`);
    if (applied.projectName) bits.push(`Project: ${applied.projectName}`);
    if (applied.partNumber) bits.push(`Part: ${applied.partNumber}`);
    if (applied.transactionType !== 'all') bits.push(`Type: ${applied.transactionType}`);
    return bits.join('  ·  ') || 'All data';
  }

  /* --------------------------------- view --------------------------------- */

  const cards = summary ? [
    { label: 'Total Products',   value: summary.totalProducts,   icon: Tags },
    { label: 'Total Stock (qty)', value: summary.totalStock,     icon: Boxes },
    { label: 'Inward Qty',       value: summary.totalInwardQty,  icon: PackageOpen },
    { label: 'Outward Qty',      value: summary.totalOutwardQty, icon: PackageMinus },
    { label: 'Available Stock',  value: summary.availableStock,  icon: Layers },
    { label: `Low Stock Items (≤ ${summary.lowStockThreshold})`, value: summary.lowStockItems, icon: AlertTriangle },
    { label: 'Pending / Active', value: summary.pendingTransactions, icon: Clock },
    { label: 'Entities',         value: summary.totalEntities,   icon: Building2 },
  ] : [];

  return (
    <div className="space-y-5">
      {/* ------------------------------ Filters ------------------------------ */}
      <div className="zip-card p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          {DATE_PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setFilters((f) => ({ ...f, preset: p.key }))}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all
                ${filters.preset === p.key
                  ? 'bg-zip-red text-white border-zip-red'
                  : 'border-zip-border text-zip-muted hover:border-zip-red/40 hover:text-zip-text'}`}
            >
              {p.label}
            </button>
          ))}
          {filters.preset === 'custom' && (
            <div className="flex items-center gap-2 ml-1">
              <input type="date" className="zip-input text-xs py-1.5 w-40" value={filters.from}
                onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))} />
              <span className="text-zip-muted text-xs">to</span>
              <input type="date" className="zip-input text-xs py-1.5 w-40" value={filters.to}
                onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))} />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
          <div>
            <label className="block text-[11px] font-medium text-zip-muted mb-1">Entity</label>
            <select className="zip-input text-sm" value={filters.client}
              onChange={(e) => setFilters((f) => ({ ...f, client: e.target.value }))}>
              <option value="">All entities</option>
              {options.clients.map((c) => <option key={c._id} value={c._id}>{c.name} ({c.code})</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-medium text-zip-muted mb-1">Category</label>
            <select className="zip-input text-sm" value={filters.category}
              onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))}>
              <option value="">All categories</option>
              {options.categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-medium text-zip-muted mb-1">Project Name</label>
            <select className="zip-input text-sm" value={filters.projectName}
              onChange={(e) => setFilters((f) => ({ ...f, projectName: e.target.value }))}>
              <option value="">All projects</option>
              {options.projects.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-medium text-zip-muted mb-1">Part Number</label>
            <input className="zip-input text-sm font-mono" placeholder="CAM-0000-2185"
              value={filters.partNumber}
              onChange={(e) => setFilters((f) => ({ ...f, partNumber: e.target.value }))} />
          </div>
          <div>
            <label className="block text-[11px] font-medium text-zip-muted mb-1">Transaction Type</label>
            <select className="zip-input text-sm" value={filters.transactionType}
              onChange={(e) => setFilters((f) => ({ ...f, transactionType: e.target.value }))}>
              {TX_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-medium text-zip-muted mb-1">Search</label>
            <input className="zip-input text-sm" placeholder="Item, ref, description…"
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }} />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <button type="button" onClick={applyFilters} className="btn-primary text-sm">Apply Filters</button>
          <button type="button" onClick={resetFilters} className="btn-ghost text-sm flex items-center gap-1.5">
            <RotateCcw size={13} /> Reset
          </button>

          {canExport && (
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-[11px] text-zip-muted hidden sm:inline">Export current filters:</span>
              <button type="button" onClick={() => downloadServerFile('xlsx')} disabled={!!exporting}
                className="btn-ghost text-xs flex items-center gap-1.5 disabled:opacity-50">
                <FileSpreadsheet size={13} /> {exporting === 'xlsx' ? 'Exporting…' : 'Excel'}
              </button>
              <button type="button" onClick={() => downloadServerFile('csv')} disabled={!!exporting}
                className="btn-ghost text-xs flex items-center gap-1.5 disabled:opacity-50">
                <FileDown size={13} /> {exporting === 'csv' ? 'Exporting…' : 'CSV'}
              </button>
              <button type="button" onClick={exportPdf} disabled={!!exporting}
                className="btn-ghost text-xs flex items-center gap-1.5 disabled:opacity-50">
                <FileText size={13} /> {exporting === 'pdf' ? 'Exporting…' : 'PDF'}
              </button>
            </div>
          )}
        </div>

        <p className="text-[11px] text-zip-muted/80 font-mono border-t border-zip-border pt-3">
          Showing: {activeFilterSummary()}
        </p>
      </div>

      {error && (
        <div className="zip-card p-5 border-l-4 border-l-zip-red">
          <p className="text-sm text-zip-text font-medium">{error}</p>
          <button onClick={loadOverview} className="btn-ghost text-xs mt-3">Try again</button>
        </div>
      )}

      {/* --------------------------- Summary cards --------------------------- */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => <div key={i} className="zip-card h-[104px] skeleton" />)}
        </div>
      ) : summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map((c) => <StatCard key={c.label} {...c} />)}
        </div>
      )}

      {/* ------------------------------ Charts ------------------------------ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card
          title="Inward vs Outward Quantity"
          subtitle={analytics ? `Grouped by ${analytics.bucket}` : undefined}
          className="lg:col-span-2"
        >
          {loading ? <ChartSkeleton height={280} />
            : !analytics?.trend?.length ? <ChartEmpty height={280} />
              : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={analytics.trend} barGap={4}>
                    <CartesianGrid vertical={false} stroke="#E2E8F0" strokeDasharray="3 3" />
                    <XAxis dataKey="period" tick={axisTick} axisLine={false} tickLine={false} />
                    <YAxis tick={axisTick} axisLine={false} tickLine={false} width={56}
                      tickFormatter={(v) => (v >= 1000 ? `${v / 1000}k` : v)} />
                    <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(15,23,42,0.03)' }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="inward" name="Inward" fill="#E8192C" radius={[3, 3, 0, 0]} maxBarSize={28} />
                    <Bar dataKey="outward" name="Outward" fill="#1D4ED8" radius={[3, 3, 0, 0]} maxBarSize={28} />
                  </BarChart>
                </ResponsiveContainer>
              )}
        </Card>

        <Card title="Stock Movement Over Time" subtitle="Cumulative inward and outward">
          {loading ? <ChartSkeleton />
            : !analytics?.trend?.length ? <ChartEmpty />
              : (
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={analytics.trend}>
                    <CartesianGrid vertical={false} stroke="#E2E8F0" strokeDasharray="3 3" />
                    <XAxis dataKey="period" tick={axisTick} axisLine={false} tickLine={false} />
                    <YAxis tick={axisTick} axisLine={false} tickLine={false} width={56}
                      tickFormatter={(v) => (v >= 1000 ? `${v / 1000}k` : v)} />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="inward" name="Inward" stroke="#E8192C" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="outward" name="Outward" stroke="#1D4ED8" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
        </Card>

        <Card title="Category-wise Stock" subtitle="Live quantity on hand">
          {loading ? <ChartSkeleton />
            : !analytics?.stockByCategory?.length ? <ChartEmpty />
              : (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={analytics.stockByCategory} dataKey="qty" nameKey="category"
                      cx="50%" cy="50%" innerRadius={52} outerRadius={88} paddingAngle={2}>
                      {analytics.stockByCategory.map((_, i) => (
                        <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<ChartTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
        </Card>

        <Card title="Entity-wise Inward / Outward" subtitle="Top entities by movement">
          {loading ? <ChartSkeleton />
            : !analytics?.entityWise?.length ? <ChartEmpty />
              : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={analytics.entityWise} layout="vertical" margin={{ left: 8 }}>
                    <CartesianGrid horizontal={false} stroke="#E2E8F0" strokeDasharray="3 3" />
                    <XAxis type="number" tick={axisTick} axisLine={false} tickLine={false}
                      tickFormatter={(v) => (v >= 1000 ? `${v / 1000}k` : v)} />
                    <YAxis type="category" dataKey="entity" tick={axisTick} axisLine={false}
                      tickLine={false} width={104} />
                    <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(15,23,42,0.03)' }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="inward" name="Inward" fill="#E8192C" radius={[0, 3, 3, 0]} maxBarSize={14} />
                    <Bar dataKey="outward" name="Outward" fill="#1D4ED8" radius={[0, 3, 3, 0]} maxBarSize={14} />
                  </BarChart>
                </ResponsiveContainer>
              )}
        </Card>

        <Card title="Top Moving Products" subtitle="By total inward + outward quantity">
          {loading ? <ChartSkeleton />
            : !analytics?.topMoving?.length ? <ChartEmpty />
              : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={analytics.topMoving} layout="vertical" margin={{ left: 8 }}>
                    <CartesianGrid horizontal={false} stroke="#E2E8F0" strokeDasharray="3 3" />
                    <XAxis type="number" tick={axisTick} axisLine={false} tickLine={false}
                      tickFormatter={(v) => (v >= 1000 ? `${v / 1000}k` : v)} />
                    <YAxis type="category" dataKey="item" tick={axisTick} axisLine={false}
                      tickLine={false} width={120}
                      tickFormatter={(v) => (v?.length > 18 ? `${v.slice(0, 17)}…` : v)} />
                    <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(15,23,42,0.03)' }} />
                    <Bar dataKey="moved" name="Moved" fill="#0F766E" radius={[0, 3, 3, 0]} maxBarSize={16} />
                  </BarChart>
                </ResponsiveContainer>
              )}
        </Card>

        <Card title="Product-wise Stock" subtitle="Highest quantity on hand">
          {loading ? <ChartSkeleton />
            : !analytics?.stockByProduct?.length ? <ChartEmpty />
              : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={analytics.stockByProduct} layout="vertical" margin={{ left: 8 }}>
                    <CartesianGrid horizontal={false} stroke="#E2E8F0" strokeDasharray="3 3" />
                    <XAxis type="number" tick={axisTick} axisLine={false} tickLine={false}
                      tickFormatter={(v) => (v >= 1000 ? `${v / 1000}k` : v)} />
                    <YAxis type="category" dataKey="item" tick={axisTick} axisLine={false}
                      tickLine={false} width={120}
                      tickFormatter={(v) => (v?.length > 18 ? `${v.slice(0, 17)}…` : v)} />
                    <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(15,23,42,0.03)' }} />
                    <Bar dataKey="qty" name="In stock" fill="#1D4ED8" radius={[0, 3, 3, 0]} maxBarSize={16} />
                  </BarChart>
                </ResponsiveContainer>
              )}
        </Card>

        <Card
          title="Low Stock Analysis"
          subtitle={summary ? `Items at or below ${nf(summary.lowStockThreshold)}` : undefined}
        >
          {loading ? <ChartSkeleton />
            : !analytics?.lowStockRows?.length
              ? <ChartEmpty text="No items are low on stock" />
              : (
                <div className="overflow-x-auto max-h-[240px] overflow-y-auto">
                  <table className="w-full">
                    <thead className="sticky top-0 bg-zip-surface">
                      <tr className="border-b border-zip-border">
                        <th className="zip-th">Item</th>
                        <th className="zip-th">Part No.</th>
                        <th className="zip-th text-right">Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.lowStockRows.map((r, i) => (
                        <tr key={i} className="border-b border-zip-border last:border-0">
                          <td className="zip-td text-xs">{r.item || '—'}</td>
                          <td className="zip-td font-mono text-xs text-zip-muted">{r.partNumber || '—'}</td>
                          <td className="zip-td text-right">
                            <span className={r.qty === 0 ? 'chip-red' : 'chip-amber'}>
                              {nf(r.qty)} {r.unit || ''}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
        </Card>
      </div>

      {/* -------------------------- Detailed report -------------------------- */}
      <div className="zip-card overflow-hidden">
        <div className="px-5 py-4 border-b border-zip-border flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-semibold text-zip-text tracking-tight text-sm">Detailed Transaction Report</h3>
            <p className="text-[11px] text-zip-muted mt-0.5">
              {txLoading ? 'Loading…' : `${nf(tx.total)} line(s) · Inward ${nf(tx.totals?.inwardQty)} · Outward ${nf(tx.totals?.outwardQty)}`}
            </p>
          </div>
          {tx.pages > 1 && (
            <div className="flex items-center gap-2">
              <button className="btn-ghost text-xs disabled:opacity-40"
                disabled={page <= 1 || txLoading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}>Previous</button>
              <span className="text-xs font-mono text-zip-muted">Page {tx.page} of {tx.pages}</span>
              <button className="btn-ghost text-xs disabled:opacity-40"
                disabled={page >= tx.pages || txLoading}
                onClick={() => setPage((p) => p + 1)}>Next</button>
            </div>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-zip-surface border-b border-zip-border">
                {['Date', 'Type', 'Reference', 'Entity', 'Product / Item', 'Project Name',
                  'Part Number', 'Category', 'UOM', 'Invoice Qty', 'Inward Qty', 'Outward Qty', 'Status']
                  .map((h) => <th key={h} className="zip-th whitespace-nowrap">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {txLoading ? (
                [...Array(6)].map((_, i) => (
                  <tr key={i}><td colSpan={13} className="zip-td"><div className="h-6 skeleton rounded" /></td></tr>
                ))
              ) : tx.rows.length === 0 ? (
                <tr>
                  <td colSpan={13} className="zip-td text-center text-zip-muted py-12">
                    No transactions match the selected filters.
                  </td>
                </tr>
              ) : tx.rows.map((r, i) => (
                <tr key={i} className="zip-tr border-b border-zip-border last:border-0">
                  <td className="zip-td font-mono text-xs whitespace-nowrap">
                    {r.date ? format(new Date(r.date), 'dd MMM yyyy') : '—'}
                  </td>
                  <td className="zip-td">
                    <span className={r.type === 'inward' ? 'chip-green' : 'chip-blue'}>{r.type}</span>
                  </td>
                  <td className="zip-td font-mono text-xs text-zip-red">{r.ref || '—'}</td>
                  <td className="zip-td text-xs">{r.entityName || '—'}</td>
                  <td className="zip-td text-xs font-medium">{r.itemName || '—'}</td>
                  <td className="zip-td font-mono text-xs text-zip-muted">{r.projectName || '—'}</td>
                  <td className="zip-td font-mono text-xs">{r.partNumber || '—'}</td>
                  <td className="zip-td text-xs text-zip-muted">{r.category || '—'}</td>
                  <td className="zip-td font-mono text-xs text-zip-muted">{r.uom || '—'}</td>
                  <td className="zip-td font-mono text-xs text-right">{r.invoiceQty ? nf(r.invoiceQty) : '—'}</td>
                  <td className="zip-td font-mono text-xs text-right">{r.inwardQty ? nf(r.inwardQty) : '—'}</td>
                  <td className="zip-td font-mono text-xs text-right">{r.outwardQty ? nf(r.outwardQty) : '—'}</td>
                  <td className="zip-td text-xs capitalize text-zip-muted">
                    {String(r.status || '—').replace(/_/g, ' ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ReportsAnalytics;
